import {
  ComponentClass, createElement,
  FunctionComponent,
  HtmlHTMLAttributes,
  memo,
  NamedExoticComponent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import classNames from "classnames";
import { useTabsContext } from "../TabsContext";
import { TabHeaderButton } from "../TabsHeader";
import { TabStrategyType } from "../types";

import styles from "./style.module.scss";

export type TabProps = {
  header: string,
  children?: ReactNode,
  contentClassName?: string,
  tabHeaderClassName?: string
  disabled?: boolean,
  id?: string
  strategy?: TabStrategyType,
  order?: number,
  tabHeaderButtonComponent?: string | FunctionComponent | ComponentClass | NamedExoticComponent
} & HtmlHTMLAttributes<HTMLButtonElement>

export const Tab = memo<TabProps>((
  {
    children,
    header,
    disabled,
    contentClassName,
    tabHeaderClassName,
    id = header,
    strategy,
    order = 0,
    tabHeaderButtonComponent = TabHeaderButton,
    ...rest
  }) => {
  const [ wasActive, setWasActive ] = useState(false);

  const { activeTab, setActiveTab, registerTab, unregisterTab, strategy: tabsStrategy } = useTabsContext();

  const onClick = useCallback(() => setActiveTab!(id), [ setActiveTab, id ]);

  useEffect(() => {
    registerTab(id);
    return () => unregisterTab(id);
  }, [ registerTab, id, unregisterTab ]);

  const isActiveTab = activeTab === id;
  const activeStrategy = strategy ?? tabsStrategy;

  useEffect(() => {
    if (isActiveTab) {
      setWasActive(true);
    }
  }, [ isActiveTab ]);

  return (
    <>
      {
        createElement(
          tabHeaderButtonComponent,
          {},
          <button
            {...rest}
            style={{ order }}
            type="button"
            tabIndex={-1}
            onClick={onClick}
            disabled={disabled}
            data-tab-active={isActiveTab}
            data-tab-disabled={!!disabled}
            className={classNames(styles.tab, tabHeaderClassName, {
              [styles.selectedTab]: isActiveTab,
              [styles.disabledTab]: disabled,
            })}
          >
            <span className={styles.label}>{header}</span>
          </button>,
        )
      }
      {
        activeStrategy === "hide" &&
        <div className={classNames(contentClassName, styles.content, { [styles.invisibleTab]: !isActiveTab })}>
          {children}
        </div>
      }
      {
        (activeStrategy === "collapse" && isActiveTab) &&
        <div className={classNames(contentClassName, styles.content)}>
          {children}
        </div>
      }
      {
        activeStrategy === "once" &&
        <div className={classNames(contentClassName, styles.content, { [styles.invisibleTab]: !isActiveTab })}>
          {wasActive && children}
        </div>
      }
    </>
  );
});

export default Tab;


